import { ConnectionManager } from './connection'
import { EventManager, type EventType } from './events'
import { WS_METHODS } from './protocol'
import type {
  ConnectionState,
  TournamentWSClientConfig,
  TournamentEvent,
  TournamentEventPriceUpdated,
  Tournament,
  PlayerMetrics,
  Operation,
  TimeSeriesRequest,
  TimeSeriesResponse,
  PlaceMarketOrderParams,
  PlacePendingOrderParams,
  UpdateOperationParams,
  PushedEvent,
  Order,
  OperationClosed,
  OperationOpen,
  FastForwardResponse,
} from './types'
import { RequestError, AuthenticationError, TimeoutError, ConnectionError } from './errors'

function isLocalhost(serverDomain: string): boolean {
  const hostWithPort = serverDomain.split('/')[0]
  if (hostWithPort.startsWith('[')) {
    return hostWithPort.startsWith('[::1]') || hostWithPort.startsWith('[0:0:0:0:0:0:0:1]')
  }
  const hostname = hostWithPort.split(':')[0]
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0'
}

function buildWsUrl(config: TournamentWSClientConfig): string {
  let serverDomain = config.serverDomain.trim()
  let protocol: 'ws' | 'wss' | undefined

  const protocolMatch = serverDomain.match(/^(wss?):\/\//i)
  if (protocolMatch) {
    protocol = protocolMatch[1].toLowerCase() as 'ws' | 'wss'
    serverDomain = serverDomain.replace(/^(wss?):\/\//i, '')
  } else if (config.wsProtocol) {
    protocol = config.wsProtocol
  } else if (typeof window !== 'undefined' && window.location?.protocol === 'https:') {
    protocol = 'wss'
  } else if (isLocalhost(serverDomain)) {
    protocol = 'ws'
  } else {
    protocol = 'wss'
  }

  return `${protocol}://${serverDomain}/ws/tournaments/${config.tournamentId}/${config.userId}/connect`
}

export class TournamentWSClient {
  private connection: ConnectionManager
  private eventManager: EventManager = new EventManager()
  private responseMap: Map<
    string,
    {
      resolve: (value: any) => void
      reject: (error: Error) => void
      timeout: ReturnType<typeof setTimeout>
    }
  > = new Map()
  private requestIdCounter = 0
  private chartDataAbortController: AbortController | null = null
  private chartDataRequestId: string | null = null

  constructor(config: TournamentWSClientConfig) {
    const wsUrl = buildWsUrl(config)

    this.connection = new ConnectionManager({
      url: wsUrl,
      ticket: config.ticket,
      autoReconnect: config.autoReconnect ?? true,
      reconnectInterval: config.reconnectInterval ?? 1000,
      reconnectMaxAttempts: config.reconnectMaxAttempts ?? 5,
      enableHeartbeat: config.enableHeartbeat ?? true,
    })

    this.connection.onMessage((data) => this.handleMessage(data))
  }

  async connect(): Promise<void> {
    await this.connection.connect()
  }

  async disconnect(): Promise<void> {
    await this.connection.disconnect()
    this.clearPendingRequests()
    this.eventManager.clear()
  }

  isConnected(): boolean {
    return this.connection.getConnectionState() === 'connected'
  }

  getConnectionState(): ConnectionState {
    return this.connection.getConnectionState()
  }

  setAutoReconnect(enabled: boolean): void {
    this.connection.setAutoReconnect(enabled)
  }

  onStateChange(handler: (state: ConnectionState) => void): () => void {
    return this.connection.onStateChange(handler)
  }

  async placeMarketOrder(params: PlaceMarketOrderParams): Promise<OperationOpen> {
    return this.sendRequest<OperationOpen>(WS_METHODS.PLACE_MARKET_ORDER, params)
  }

  async placePendingOrder(params: PlacePendingOrderParams): Promise<Order> {
    return this.sendRequest<Order>(WS_METHODS.PLACE_PENDING_ORDER, params)
  }

  async updateOperation(params: UpdateOperationParams): Promise<Operation> {
    return this.sendRequest<Operation>(WS_METHODS.UPDATE_OPERATION, params)
  }

  async closeOpenPosition(orderId: string): Promise<OperationClosed> {
    return this.sendRequest<OperationClosed>(WS_METHODS.CLOSE_OPEN_POSITION, { orderId })
  }

  async cancelPendingOrder(orderId: string): Promise<Order> {
    return this.sendRequest<Order>(WS_METHODS.CANCEL_PENDING_ORDER, { orderId })
  }

  async listAllOperations(): Promise<Operation[]> {
    return this.sendRequest<Operation[]>(WS_METHODS.LIST_ALL_OPERATIONS)
  }

  async info(): Promise<Tournament> {
    return this.sendRequest<Tournament>(WS_METHODS.INFO)
  }

  async metrics(): Promise<PlayerMetrics[]> {
    return this.sendRequest<PlayerMetrics[]>(WS_METHODS.METRICS)
  }

  async timeSeries(params: TimeSeriesRequest): Promise<TimeSeriesResponse> {
    return this.sendRequest<TimeSeriesResponse>(WS_METHODS.TIME_SERIES, params)
  }

  /**
   * Fetch fresh OHLC candle data for the specified time window.
   * Used during fast-forward in historical replay to update the chart with new data.
   *
   * Features:
   * - Aborts previous requests to handle rapid successive fast-forwards
   * - 3-second timeout with graceful failure (caller proceeds with old data)
   * - Returns fresh candle data that the data provider will distribute to chart subscribers
   *
   * Flow: FAST_FORWARD_RESPONSE → TournamentApp → wsClient.refetchChartData()
   *       → data provider notified → chart updated with new candles
   *       → entry/SL/TP lines and markers reposition based on updated timestamps
   *
   * @param startTimeMs - Start of time window in milliseconds (UTC)
   * @param endTimeMs - End of time window in milliseconds (UTC)
   * @returns Fresh TimeSeriesResponse with updated candle data
   * @throws TimeoutError if fetch exceeds 3 seconds
   * @throws RequestError if server returns an error
   */
  async refetchChartData(startTimeMs: number, endTimeMs: number): Promise<TimeSeriesResponse> {
    // Abort any previous chart data fetch to handle rapid successive fast-forwards
    if (this.chartDataAbortController) {
      this.chartDataAbortController.abort()
    }
    // Clean up the previous chart data request from responseMap
    if (this.chartDataRequestId && this.responseMap.has(this.chartDataRequestId)) {
      const pending = this.responseMap.get(this.chartDataRequestId)
      if (pending) {
        clearTimeout(pending.timeout)
        this.responseMap.delete(this.chartDataRequestId)
      }
    }

    // Create a new AbortController for this fetch
    this.chartDataAbortController = new AbortController()
    const abortSignal = this.chartDataAbortController.signal

    // Create a timeout that will abort the request after 3 seconds
    const timeoutId = setTimeout(() => {
      this.chartDataAbortController?.abort()
    }, 3000)

    try {
      // Convert milliseconds to ISO timestamp strings for the server
      const startDate = new Date(startTimeMs).toISOString()
      const endDate = new Date(endTimeMs).toISOString()

      const params: TimeSeriesRequest = {
        symbol: '', // The server should infer symbol from context, but we keep structure
        start_date: startDate,
        end_date: endDate,
        order: 'asc',
      }

      // Create the promise for the request, capturing the request ID
      const requestPromise = this.sendRequest<TimeSeriesResponse>(
        WS_METHODS.TIME_SERIES,
        params,
        (id) => {
          this.chartDataRequestId = id
        }
      )

      // Race between request completion and abort signal
      const abortPromise = new Promise<TimeSeriesResponse>((_, reject) => {
        abortSignal.addEventListener('abort', () => {
          reject(new TimeoutError('Chart data fetch aborted or timed out'))
        })
      })

      const result = await Promise.race([requestPromise, abortPromise])
      clearTimeout(timeoutId)
      return result
    } catch (error) {
      clearTimeout(timeoutId)
      // Clean up the pending chart data request on abort/timeout
      if (this.chartDataRequestId && this.responseMap.has(this.chartDataRequestId)) {
        const pending = this.responseMap.get(this.chartDataRequestId)
        if (pending) {
          clearTimeout(pending.timeout)
          this.responseMap.delete(this.chartDataRequestId)
        }
      }
      throw error
    }
  }

  on(
    eventType: 'TOURNAMENT_EVENT',
    handler: (event: TournamentEvent) => void
  ): () => void
  on(
    eventType: 'PRICE_UPDATED',
    handler: (event: TournamentEventPriceUpdated) => void
  ): () => void
  on(
    eventType: 'FAST_FORWARD_RESPONSE',
    handler: (event: FastForwardResponse) => void
  ): () => void
  on(
    eventType: 'FAST_FORWARD_ERROR',
    handler: (event: { message: string }) => void
  ): () => void
  on(eventType: EventType, handler: (event: any) => void): () => void {
    const subscription = this.eventManager.on(eventType as EventType, handler)
    return () => subscription.unsubscribe()
  }

  private async sendRequest<T>(method: string, params?: any, onRequestId?: (id: string) => void): Promise<T> {
    const id = this.generateRequestId()

    return new Promise<T>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.responseMap.delete(id)
        reject(new TimeoutError(`Request ${id} timed out`))
      }, 30000)

      this.responseMap.set(id, { resolve, reject, timeout })

      if (onRequestId) {
        onRequestId(id)
      }

      const request = {
        id,
        method,
        params: params || {},
      }

      this.connection.send(JSON.stringify(request)).catch((error) => {
        this.responseMap.delete(id)
        clearTimeout(timeout)
        reject(
          error instanceof ConnectionError ? error : new ConnectionError('Failed to send request')
        )
      })
    })
  }

  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data)

      if ('id' in message) {
        this.handleResponse(message)
      } else if ('event' in message) {
        this.handleEvent(message)
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error)
      // Emit parse error event to trigger reconciliation
      this.eventManager.emit('PARSE_ERROR' as EventType, {})
    }
  }

  private handleResponse(message: {
    id: string
    result?: unknown
    error?: { code: number; message: string }
  }): void {
    const pending = this.responseMap.get(message.id)
    if (!pending) return

    this.responseMap.delete(message.id)
    clearTimeout(pending.timeout)

    if (message.error) {
      const error = message.error
      if (error.code === 401) {
        pending.reject(new AuthenticationError(error.message))
      } else {
        pending.reject(new RequestError(error.message, error.code))
      }
    } else {
      pending.resolve(message.result)
    }
  }

  private handleEvent(message: { event: string; payload: unknown }): void {
    this.eventManager.emit(message.event as EventType, message)
  }

  private generateRequestId(): string {
    return `req-${Date.now()}-${++this.requestIdCounter}`
  }

  private clearPendingRequests(): void {
    this.responseMap.forEach(({ reject, timeout }) => {
      clearTimeout(timeout)
      reject(new ConnectionError('Connection closed'))
    })
    this.responseMap.clear()
  }
}
